import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useAuthPopup } from '../hooks/useAuthPopup';
import SaveSearchModal from './SaveSearchModal';
import BuyBoxModal from './BuyBoxModal';
import AddressSearchInput from './AddressSearchInput';
import { FILTER_CONFIG } from '../config/filters';
import { API_URL } from '../config/api';
import './FilterBar.css';

// Sub-component for Keyword Filter to validly use Hooks
const KeywordFilter = ({ item, localFilters, onInputChange }) => {
    const currentVal = localFilters[item.key] || '';
    const selectedTags = currentVal.split(',').map(s => s.trim()).filter(Boolean);

    // Track the text input for adding custom tags
    const [inputText, setInputText] = useState('');

    const addTag = (tag) => {
        if (!tag.trim()) return;
        const newTags = selectedTags.includes(tag.trim())
            ? selectedTags
            : [...selectedTags, tag.trim()];
        onInputChange({ target: { name: item.key, value: newTags.join(', ') } });
    };

    const removeTag = (tag) => {
        const newTags = selectedTags.filter(t => t !== tag);
        onInputChange({ target: { name: item.key, value: newTags.join(', ') } });
    };

    const toggleTag = (tag) => {
        if (selectedTags.includes(tag)) {
            removeTag(tag);
        } else {
            addTag(tag);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && inputText.trim()) {
            e.preventDefault();
            addTag(inputText.trim());
            setInputText('');
        }
        // Backspace on empty input removes last tag
        if (e.key === 'Backspace' && !inputText && selectedTags.length > 0) {
            removeTag(selectedTags[selectedTags.length - 1]);
        }
    };

    return (
        <div className="keyword-input-section">
            <p className="keyword-section-label">Keyword search</p>

            {/* Selected Tags Container */}
            <div className="keyword-selected-container" onClick={(e) => e.currentTarget.querySelector('input')?.focus()}>
                {selectedTags.map(tag => (
                    <span key={tag} className="keyword-selected-tag">
                        {tag}
                        <button
                            className="keyword-tag-remove"
                            onClick={(e) => { e.stopPropagation(); removeTag(tag); }}
                            title="Remove"
                        >×</button>
                    </span>
                ))}
                <input
                    type="text"
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={selectedTags.length === 0 ? item.placeholder : ''}
                    className="keyword-inline-input"
                    autoComplete="off"
                />
                {selectedTags.length > 0 && (
                    <button
                        className="keyword-clear-all-btn"
                        onClick={(e) => { e.stopPropagation(); onInputChange({ target: { name: item.key, value: '' } }); }}
                        title="Clear all"
                    >×</button>
                )}
            </div>

            {/* Predefined Options */}
            <div className="keyword-chips-grid">
                {item.options.map(opt => {
                    const isSelected = selectedTags.includes(opt.value);
                    return (
                        <button
                            key={opt.value}
                            className={`keyword-chip ${isSelected ? 'selected' : ''}`}
                            onClick={() => toggleTag(opt.value)}
                        >
                            <span className="chip-icon">{isSelected ? '✓' : '+'}</span>
                            {opt.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

const FilterBar = ({ filters, onFilterChange }) => {
    // Local state for debounced inputs
    const [localFilters, setLocalFilters] = useState(filters);
    const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
    const [isBuyBoxModalOpen, setIsBuyBoxModalOpen] = useState(false);

    // Active Popover State (ID of the filter/group being edited)
    const [activePopover, setActivePopover] = useState(null);
    const popoverRef = useRef(null);

    const { user, session } = useAuth();
    const { showToast } = useToast();
    const { openAuthPopup } = useAuthPopup();

    // Sync local state when parent filters change
    useEffect(() => {
        if (JSON.stringify(filters) !== JSON.stringify(localFilters)) {
            setLocalFilters(filters);
        }
        // eslint-disable-next-line
    }, [filters]);

    // Close popovers when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (popoverRef.current && !popoverRef.current.contains(event.target)) {
                if (!event.target.closest('.filter-chip-btn')) {
                    setActivePopover(null);
                }
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Debounce for text inputs
    useEffect(() => {
        const timer = setTimeout(() => {
            const updates = {};
            let hasChanges = false;
            const skipKeys = ['city', 'state', 'zip_code']; // Set immediately via onFilterChange
            Object.keys(localFilters).forEach(key => {
                if (skipKeys.includes(key)) return;
                if (localFilters[key] !== filters[key]) {
                    updates[key] = localFilters[key];
                    hasChanges = true;
                }
            });
            if (hasChanges) onFilterChange(updates);
        }, 500);
        return () => clearTimeout(timer);
    }, [localFilters, filters, onFilterChange]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (value < 0) return; // Basic validation
        setLocalFilters(prev => ({ ...prev, [name]: value }));
    };

    const handlePillClick = (key, value) => {
        const newValue = value === localFilters[key] ? '' : value;
        onFilterChange({ [key]: newValue });
    };

    const handleCheckboxChange = (e, key) => {
        const { value, checked } = e.target;
        let current = (localFilters[key] || '').split(',').filter(Boolean);
        if (checked) current.push(value);
        else current = current.filter(t => t !== value);
        onFilterChange({ [key]: current.join(',') });
    };

    const handleReset = () => {
        const keysToReset = {};
        FILTER_CONFIG.forEach(item => {
            if (item.key) keysToReset[item.key] = '';
            if (item.keys) item.keys.forEach(k => { keysToReset[k] = ''; });
            if (item.minKey) keysToReset[item.minKey] = '';
            if (item.maxKey) keysToReset[item.maxKey] = '';
        });
        // Keep location filters (city/state/zip) — only reset non-location filters
        delete keysToReset.city;
        delete keysToReset.state;
        delete keysToReset.zip_code;
        onFilterChange(keysToReset);
        setLocalFilters(prev => ({ ...prev, ...keysToReset }));
    };

    const handleSaveSearchClick = () => {
        if (!user) {
            openAuthPopup(() => setIsSaveModalOpen(true));
            return;
        }
        setIsSaveModalOpen(true);
    };

    const handleSaveSearchSubmit = async ({ name, email_frequency, cc_emails }) => {
        try {
            const response = await fetch(`${API_URL}/buy-boxes`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session?.access_token}`
                },
                body: JSON.stringify({ name, criteria: filters, email_frequency, cc_emails: cc_emails || [] })
            });
            if (response.ok) {
                showToast('Search saved successfully!', 'success');
                setIsSaveModalOpen(false);
            } else {
                showToast('Failed to save search.', 'error');
            }
        } catch (error) {
            console.error(error);
            showToast('Error saving search.', 'error');
        }
    };

    // --- Layout Logic ---
    // Helper to check if a filter has an active value
    const hasValue = (item) => {
        if (!item) return false;
        if (item.key && localFilters[item.key]) return true;
        if (item.minKey && localFilters[item.minKey]) return true;
        if (item.maxKey && localFilters[item.maxKey]) return true;
        // Check grouped sub-items logic manually if passed generic object
        return false;
    };

    // Label Generators
    const getLabel = (id) => {
        if (id === 'price') {
            const min = localFilters['min_price'];
            const max = localFilters['max_price'];
            if (min && max) return `$${(min / 1000).toFixed(0)}k-$${(max / 1000).toFixed(0)}k`;
            if (min) return `>$${(min / 1000).toFixed(0)}k`;
            if (max) return `<$${(max / 1000).toFixed(0)}k`;
            return 'Price';
        }
        if (id === 'roi') {
            const min = localFilters['min_roi'];
            const max = localFilters['max_roi'];
            if (min && max) return `${min}%-${max}% ROI`;
            if (min) return `>${min}% ROI`;
            return 'ROI %';
        }
        if (id === 'rooms') {
            const beds = localFilters['min_beds'];
            const baths = localFilters['min_baths'];
            if (beds && baths) return `${beds}+ bd, ${baths}+ ba`;
            if (beds) return `${beds}+ bd`;
            if (baths) return `${baths}+ ba`;
            return 'Rooms';
        }
        if (id === 'property_type') {
            const val = localFilters['property_types'];
            if (val) {
                const count = val.split(',').length;
                return count > 1 ? `${count} Types` : '1 Type';
            }
            return 'Home Type';
        }
        if (id === 'features') {
            const val = localFilters['feature_keywords'];
            if (val) {
                return val.length > 15 ? val.substring(0, 12) + '...' : val;
            }
            return 'Features';
        }
        if (id === 'investment_types') {
            const val = localFilters['investment_types'];
            if (val) {
                const count = val.split(',').filter(Boolean).length;
                return count > 1 ? `${count} Types` : '1 Type';
            }
            return 'Investment Type';
        }
        return 'More';
    };

    // Render Popover Content
    const renderPopoverContent = (id, items) => {
        return (
            <div className="popover-content">
                {items.map(item => (
                    <div key={item.id} className="popover-section">
                        {items.length > 1 && <h4 className="popover-section-title">{item.label}</h4>}

                        {item.type === 'range' && (
                            <div className="range-inputs">
                                <input
                                    type="number"
                                    name={item.minKey}
                                    placeholder="Min"
                                    value={localFilters[item.minKey] || ''}
                                    onChange={handleInputChange}
                                    className="std-input"
                                />
                                <span>-</span>
                                <input
                                    type="number"
                                    name={item.maxKey}
                                    placeholder="Max"
                                    value={localFilters[item.maxKey] || ''}
                                    onChange={handleInputChange}
                                    className="std-input"
                                />
                            </div>
                        )}

                        {item.type === 'pills' && (
                            <div className="pills">
                                {item.options.map(opt => (
                                    <button
                                        key={opt.value}
                                        className={`pill ${localFilters[item.key] === opt.value ? 'active' : ''}`}
                                        onClick={() => handlePillClick(item.key, opt.value)}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        )}

                        {item.type === 'checkbox_group' && (
                            <div className="checkbox-list">
                                {item.options.map(opt => (
                                    <label key={opt.value} className="checkbox-row">
                                        <input
                                            type="checkbox"
                                            value={opt.value}
                                            checked={(localFilters[item.key] || '').split(',').includes(opt.value)}
                                            onChange={(e) => handleCheckboxChange(e, item.key)}
                                        />
                                        {opt.label}
                                    </label>
                                ))}
                            </div>
                        )}

                        {item.type === 'input' && (
                            <input
                                type={item.inputType || 'text'}
                                name={item.key}
                                value={localFilters[item.key] || ''}
                                onChange={handleInputChange}
                                placeholder={item.placeholder}
                                className="std-input full-width"
                            />
                        )}

                        {item.type === 'keyword_input' && (
                            <KeywordFilter
                                item={item}
                                localFilters={localFilters}
                                onInputChange={handleInputChange}
                            />
                        )}
                    </div>
                ))}
            </div>
        );
    };

    // --- Render ---
    return (
        <div className="filter-bar">
            <div className="filter-scroll-container">

                {/* Location always first, handled by AddressSearchInput */}
                <AddressSearchInput
                    variant="bar"
                    displayValue={
                        localFilters.city
                            ? `${localFilters.city}, ${localFilters.state || ''}`
                            : localFilters.zip_code || ''
                    }
                    onSelect={(sel) => {
                        if (sel.type === 'city') onFilterChange({ city: sel.city, state: sel.state, zip_code: '' });
                        if (sel.type === 'zip') onFilterChange({ zip_code: sel.zip_code, city: '', state: '' });
                    }}
                    onClear={() => onFilterChange({ city: '', state: '', zip_code: '' })}
                />

                {/* Price */}
                <div className="filter-chip-wrapper">
                    <button
                        className={`filter-chip-btn ${hasValue(FILTER_CONFIG.find(i => i.id === 'price')) ? 'active' : ''} ${activePopover === 'price' ? 'open' : ''}`}
                        onClick={() => setActivePopover(activePopover === 'price' ? null : 'price')}
                    >
                        {getLabel('price')} <span className="chevron">▼</span>
                    </button>
                    {activePopover === 'price' && (
                        <div className="filter-popover" ref={popoverRef}>
                            {renderPopoverContent('price', [FILTER_CONFIG.find(i => i.id === 'price')])}
                        </div>
                    )}
                </div>

                {/* Rooms (Beds + Baths) */}
                <div className="filter-chip-wrapper">
                    <button
                        className={`filter-chip-btn ${hasValue(FILTER_CONFIG.find(i => i.id === 'beds')) || hasValue(FILTER_CONFIG.find(i => i.id === 'baths')) ? 'active' : ''} ${activePopover === 'rooms' ? 'open' : ''}`}
                        onClick={() => setActivePopover(activePopover === 'rooms' ? null : 'rooms')}
                    >
                        {getLabel('rooms')} <span className="chevron">▼</span>
                    </button>
                    {activePopover === 'rooms' && (
                        <div className="filter-popover" ref={popoverRef}>
                            {renderPopoverContent('rooms', [
                                FILTER_CONFIG.find(i => i.id === 'beds'),
                                FILTER_CONFIG.find(i => i.id === 'baths')
                            ])}
                        </div>
                    )}
                </div>

                {/* Home Type */}
                <div className="filter-chip-wrapper">
                    <button
                        className={`filter-chip-btn ${hasValue(FILTER_CONFIG.find(i => i.id === 'property_type')) ? 'active' : ''} ${activePopover === 'property_type' ? 'open' : ''}`}
                        onClick={() => setActivePopover(activePopover === 'property_type' ? null : 'property_type')}
                    >
                        {getLabel('property_type')} <span className="chevron">▼</span>
                    </button>
                    {activePopover === 'property_type' && (
                        <div className="filter-popover" ref={popoverRef}>
                            {renderPopoverContent('property_type', [FILTER_CONFIG.find(i => i.id === 'property_type')])}
                        </div>
                    )}
                </div>

                {/* Investment Type */}
                <div className="filter-chip-wrapper">
                    <button
                        className={`filter-chip-btn ${hasValue(FILTER_CONFIG.find(i => i.id === 'investment_types')) ? 'active' : ''} ${activePopover === 'investment_types' ? 'open' : ''}`}
                        onClick={() => setActivePopover(activePopover === 'investment_types' ? null : 'investment_types')}
                    >
                        {getLabel('investment_types')} <span className="chevron">▼</span>
                    </button>
                    {activePopover === 'investment_types' && (
                        <div className="filter-popover" ref={popoverRef}>
                            {renderPopoverContent('investment_types', [FILTER_CONFIG.find(i => i.id === 'investment_types')])}
                        </div>
                    )}
                </div>

                {/* ROI */}
                <div className="filter-chip-wrapper">
                    <button
                        className={`filter-chip-btn ${hasValue(FILTER_CONFIG.find(i => i.id === 'roi')) ? 'active' : ''} ${activePopover === 'roi' ? 'open' : ''}`}
                        onClick={() => setActivePopover(activePopover === 'roi' ? null : 'roi')}
                    >
                        {getLabel('roi')} <span className="chevron">▼</span>
                    </button>
                    {activePopover === 'roi' && (
                        <div className="filter-popover" ref={popoverRef}>
                            {renderPopoverContent('roi', [FILTER_CONFIG.find(i => i.id === 'roi')])}
                        </div>
                    )}
                </div>

                {/* Features (AI Feature Keywords) */}
                <div className="filter-chip-wrapper">
                    <button
                        className={`filter-chip-btn ${hasValue(FILTER_CONFIG.find(i => i.id === 'feature_keywords')) ? 'active' : ''} ${activePopover === 'features' ? 'open' : ''}`}
                        onClick={() => setActivePopover(activePopover === 'features' ? null : 'features')}
                    >
                        {getLabel('features')} <span className="chevron">▼</span>
                    </button>
                    {activePopover === 'features' && (
                        <div className="filter-popover" ref={popoverRef}>
                            {renderPopoverContent('features', [FILTER_CONFIG.find(i => i.id === 'feature_keywords')])}
                        </div>
                    )}
                </div>

                {/* More (DOM & Year Built) */}
                <div className="filter-chip-wrapper">
                    <button
                        className={`filter-chip-btn ${hasValue(FILTER_CONFIG.find(i => i.id === 'days_on_market')) || hasValue(FILTER_CONFIG.find(i => i.id === 'year_built')) ? 'active' : ''} ${activePopover === 'more' ? 'open' : ''}`}
                        onClick={() => setActivePopover(activePopover === 'more' ? null : 'more')}
                    >
                        More <span className="chevron">▼</span>
                    </button>
                    {activePopover === 'more' && (
                        <div className="filter-popover" ref={popoverRef}>
                            {renderPopoverContent('more', [
                                FILTER_CONFIG.find(i => i.id === 'days_on_market'),
                                FILTER_CONFIG.find(i => i.id === 'year_built')
                            ])}
                        </div>
                    )}
                </div>

                <div className="filter-actions-right">
                    <button className="btn-reset" onClick={handleReset}>Reset</button>
                    <button className="btn-save" onClick={handleSaveSearchClick}>Save Search</button>
                    <button className="btn-buybox" onClick={() => user ? setIsBuyBoxModalOpen(true) : openAuthPopup(() => setIsBuyBoxModalOpen(true))}>+ Buy Box</button>
                </div>
            </div>

            <SaveSearchModal
                isOpen={isSaveModalOpen}
                onClose={() => setIsSaveModalOpen(false)}
                onSave={handleSaveSearchSubmit}
            />
            <BuyBoxModal
                isOpen={isBuyBoxModalOpen}
                onClose={() => setIsBuyBoxModalOpen(false)}
                onSuccess={() => setIsBuyBoxModalOpen(false)}
            />
        </div>
    );
};

export default FilterBar;
